<template>
  <template v-if="isReadOnly">
    <div
      v-if="selectedDemographics?.length"
      class="row">
      <div
        v-if="gender?.id && gender.id !== 'A'"
        class="col-3 line-item-demographics-gender-list">
        <h4>{{ capitalize(i18n.t('names.gender', 1)) }}</h4>
        <span>
          {{ gender.name }}
        </span>
      </div>
      <div
        v-if="ageRanges?.length"
        class="col-3 line-item-age-demographics">
        <h4>{{ capitalize(i18n.t('names.age', 1)) }}</h4>
        <span>
          {{ `${first(ageRanges)}-${last(ageRanges)}` }}
        </span>
      </div>
      <div
        v-if="incomeBracketsFormatted?.length"
        class="col-3 line-item-hhi-demographics">
        <h4>{{ capitalize(i18n.t('messages.householdIncome')) }}</h4>
        <span>
          {{ `${first(incomeBracketsFormatted)}-${last(incomeBracketsFormatted)}` }}
        </span>
      </div>
      <div
        v-if="ethnicities?.length"
        class="col-3 line-item-ethnicity-demographics">
        <h4>{{ capitalize(i18n.t('names.ethnicity', 2)) }}</h4>
        <span>
          {{ ethnicities.map((eth) => eth.name).join(',') }}
        </span>
      </div>
    </div>
  </template>
  <template v-else-if="fallbackToMultiselect">
    <demographic-selector
      :base-demographics="demographics"
      :selected-demographics="selectedDemographics"
      :allow-selection="!isReadOnly"
      :error-object="$validator.messageBag.errors"
      @demographics-selected="(val) => emit('onChange', val)" />
  </template>
  <template v-else>
    <div
      v-if="genderDemographics?.length"
      class="gender">
      <div class="section-label">{{ capitalize(i18n.t('names.gender', 1)) }}</div>
      <div id="edit_lineitem_gender">
        <SelectableTiles
          class="well"
          :items="genderOptions"
          :should-display-labels="true"
          :should-display-icons="true"
          :should-display-checkmark-on-selected-items="true"
          label-property-name="name"
          icon-property-name="icon"
          icon-size="12rem"
          :selected-items="[gender]"
          :is-multiselect="false"
          :is-read-only="isReadOnly"
          :allow-empty="false"
          :justify-content-left="true"
          tile-width=""
          tile-padding="1rem 1rem 2rem"
          @selection-changed="onGenderSelectionChange" />
      </div>
    </div>

    <div
      v-if="ageRanges?.length"
      class="age">
      <div class="section-label">{{ capitalize(i18n.t('names.age', 1)) }}</div>
      <div
        id="age-gender-hhi-slider"
        class="well">
        <div class="slider-container">
          <IconColoredTeenager />
          <Slider
            v-if="age !== null"
            v-model="age"
            :min="0"
            :min-range="1"
            :max-range="ageRanges.length"
            :contained="true"
            :max="ageRanges.length - 1"
            :marks="ageMarks"
            :enable-cross="false"
            :tooltip-formatter="(val) => ageRangesFormatted[val]"
            :disabled="isReadOnly"
            :step-style="{ display: 'none' }">
            <template #label="{ label, active }">
              <div :class="['vue-slider-mark-label', 'custom-label', { active }]">
                <span>{{ ageRangesFormatted[label] }}</span>
              </div>
            </template>
          </Slider>
        </div>
      </div>
    </div>

    <div
      v-if="incomeBrackets?.length"
      class="hhi">
      <div class="section-label">
        {{ capitalize(i18n.t('messages.householdIncome')) }}
      </div>
      <div
        id="age-gender-hhi-slider-2"
        class="well">
        <div class="slider-container">
          <IconColoredLowIncome />
          <Slider
            v-if="income !== null"
            v-model="income"
            :min="0"
            :min-range="1"
            :max-range="incomeBrackets.length"
            :contained="true"
            :max="incomeBrackets.length - 1"
            :marks="incomeMarks"
            :enable-cross="false"
            :tooltip-formatter="(val) => incomeBracketsFormatted[val]"
            :disabled="isReadOnly"
            :step-style="{ display: 'none' }">
            <template #label="{ label, active }">
              <div :class="['vue-slider-mark-label', 'custom-label', { active }]">
                <span>{{ incomeBracketsFormatted[label] }}</span>
              </div>
            </template>
          </Slider>
        </div>
      </div>
    </div>

    <div
      v-if="ethnicityOptions?.length"
      class="eth">
      <div class="section-label">
        {{ capitalize(i18n.t('names.ethnicity', 2)) }}
      </div>
      <div id="edit_lineitem_ethnicity">
        <multiselect
          v-model="ethnicities"
          :options="ethnicityOptions"
          :multiple="true"
          :allow-empty="true"
          :disabled="isReadOnly"
          label="name"
          track-by="id" />
      </div>
    </div>
  </template>
</template>

<script setup lang="ts">
  import { capitalize, computed, inject, nextTick, onMounted, ref, watch } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { Multiselect } from 'vue-multiselect';
  import Slider from 'vue-slider-component';

  import { DemographicSelector } from '@/common/components/controls';
  import SelectableTiles from '@/common/components/controls/SelectableTiles.vue';
  import DemographicUtils from '@/common/components/targeting/DemographicUtils';
  import Genders from '@/common/components/targeting/Genders';
  import HouseholdIncomeRanges from '@/common/components/targeting/HouseholdIncomeRanges';
  import { DemographicTile } from '@/common/enums/DemographicTile';
  import { IconColoredLowIncome, IconColoredTeenager } from '@/common/icons/colored';
  import { Demographic } from '@/common/models';
  import { areArraysEqual, first, last, uniq, uniqBy } from '@/globals';
  import { mapOrgAppState } from '@/utils/StateMapping';
  import Validator from '@/validator/Validator';

  interface Props {
    defaultGender: unknown;
    demographics: Demographic[];
    selectedDemographics: Demographic[];
    isReadOnly: boolean;
    isCensus: boolean;
  }

  const genderCode = `${DemographicUtils.codes.demographic} ${DemographicUtils.codes.gender}`;

  // Props
  const props = withDefaults(defineProps<Props>(), {
    isReadOnly: false,
    isCensus: false,
  });
  const i18n = useI18n();
  const gender = ref<unknown>(null);
  const age = ref<number[]>([0, 0]);
  const income = ref<number[]>([0, 0]);
  const ethnicities = ref<{ name: string; demo: Demographic }[]>([]);
  const fallbackToMultiselect = ref(false);
  const $validator: Validator = inject('validator');
  const isInitializing = ref(true);

  // Emit
  const emit = defineEmits<{
    (e: 'onChange', value: { demographic: Demographic }[]): void;
    (e: 'onChangeLegacy', value: Demographic[]): void;
    (e: 'updateBetaDemos', value: any);
  }>();
  defineExpose({
    updateBetaDemos,
  });

  // Computed
  const { organizationSettings } = mapOrgAppState();
  const innerDemographics = computed(() =>
    props.demographics.length ? props.demographics : props.selectedDemographics,
  );
  const genderDemographics = computed(() =>
    innerDemographics.value?.filter((d) => DemographicUtils.isGenderDemographic(d)),
  );
  const genderOptions = computed(() =>
    Genders.options().map((gender) => {
      const updatedGender = gender;
      if (gender.id !== 'A') {
        updatedGender.entity = genderDemographics.value.find((demo) => {
          if (DemographicUtils.isCensusDemographic(demo)) {
            return demo.code?.startsWith(gender.id) || demo.code?.endsWith(gender.id);
          }
          return demo.code.split(genderCode)[1] === gender.id;
        });
      }
      return updatedGender;
    }),
  );
  const ageDemographics = computed(() => innerDemographics.value?.filter((d) => DemographicUtils.isAgeDemographic(d)));
  const hasAgeOnlyDemographics = computed(() => ageDemographics.value.some((d) => !/^[MF] /.test(d.code)));
  const hasAgeGenderDemographics = computed(() => ageDemographics.value.some((d) => /^[MF] /.test(d.code)));
  const ageRanges = computed(() => {
    const ranges = uniq(
      ageDemographics.value
        .reduce((res, d, i, arr) => {
          const range = DemographicUtils.getAgeRange(d);
          if (i < arr.length - 1) {
            return [...res, range?.start];
          } else {
            return [...res, range?.start, range?.end];
          }
        }, [])
        .filter((v) => v),
    );

    const sortRange = (rangeList) => {
      const sortedRange = [];
      const numericalRange = rangeList.filter((r) => !Number.isNaN(Number(r))).sort((a, b) => +a - +b);
      const hasPlus = rangeList.filter((r) => r === '+');

      if (numericalRange?.length) {
        sortedRange.push(...numericalRange);
      }

      if (hasPlus?.length) {
        sortedRange.push(...hasPlus);
      }

      return sortedRange;
    };

    return sortRange(ranges).reduce((arr: string[], elem: string) => {
      const last = arr.slice(-1)[0];
      if (last === elem) return arr;
      if (Number(last) === Number(elem) - 1) {
        return [...arr.slice(0, arr.length - 1), elem];
      }
      return [...arr, elem];
    }, []);
  });
  const ageRangesFormatted = computed(() =>
    ageRanges.value.map((r) => {
      if (r === '+') return '∞';
      return r;
    }),
  );
  const ageMarks = computed(() => ageRanges.value.map((_r, i) => i));
  const incomeDemographics = computed(() =>
    innerDemographics.value?.filter((d) => DemographicUtils.isIncomeDemographic(d)),
  );
  const incomeBrackets = computed(() => HouseholdIncomeRanges.optionsFormatted(incomeDemographics.value));
  const incomeBracketsFormatted = computed<string[]>(() => {
    const brackets = HouseholdIncomeRanges.options(incomeDemographics.value);
    if (!brackets || !brackets.length) return brackets;
    return brackets.map((hhi) => {
      if (hhi.includes('%')) return hhi;
      if (hhi.includes('+')) {
        const hhiRange = hhi.split('+')[0];
        return `>${i18n.n(Number(hhiRange), { notation: 'compact', key: 'currency' })}`;
      }
      return `${i18n.n(Number(hhi), { notation: 'compact', key: 'currency' })}`;
    });
  });
  const incomeMarks = computed(() => incomeBrackets.value.map((_x, i) => i));
  const ethnicityDemographics = computed<Demographic[]>(() => {
    return innerDemographics.value?.filter((d) => DemographicUtils.isEthnicityDemographic(d));
  });
  const ethnicityOptions = computed(() => {
    return ethnicityDemographics.value.map((demo) => {
      return {
        name: demo.name,
        id: demo.code,
        demo: demo,
      };
    });
  });

  //
  // Functions
  //

  function getDemographicGenderName(demo: Demographic) {
    return Genders.options().find((g) => demo.code.startsWith(g.id)).name;
  }

  // Moved here for compat, not sure if we actually need this stuff anymore. Would be nice to get rid of.
  function updateBetaDemos() {
    if (props.isReadOnly || !props.isCensus || !organizationSettings || !organizationSettings.uiEnableDemoFeatures) {
      return;
    }
    const state: any = {
      tile: DemographicTile.BASE_DEMOGRAPHICS,
      gender: gender.value,
      age: age.value,
      hhi: income.value,
      eth: ethnicities.value.map((e) => e.demo),
    };
    if (age.value[1] !== -1) state.ageRanges = ageRanges.value;
    if (income.value[1] !== -1) state.hhiRanges = incomeBrackets.value;
    emit('updateBetaDemos', state);
  }
  function checkFallbackToMultiselect(): boolean {
    // If we have multiple data providers, we show multiselect
    const uniqueDataProviders = uniqBy(props.demographics, 'dataProviderId');
    if (uniqueDataProviders.length > 1) return true;

    // Otherwise we never show multiselect for non-census
    if (!props.isCensus) return false;

    const codes = ageDemographics.value.map((x) => x.code.split(' '));
    const sortedCodeSuffixes = uniq(codes.map((c) => (c.length > 1 ? c[1] : c[0]))).sort((a, b) =>
      a.localeCompare(b, undefined, { numeric: true }),
    );

    // If there are duplicate codes, we show multiselect
    const uniqueAgeDemos = uniqBy(ageDemographics.value, 'code');
    if (uniqueAgeDemos.length !== ageDemographics.value.length) {
      return true;
    }
    // If there is a code with '+' thats not the last one, we show multiselect
    // If there is more than one code with '+', we show multiselect
    const codesWithPlus = sortedCodeSuffixes.filter((c) => c.includes('+'));
    const firstPlusIndex = sortedCodeSuffixes.findIndex((c) => c.includes('+'));
    if (codesWithPlus.length > 1 || firstPlusIndex < sortedCodeSuffixes.length - 1) {
      return true;
    }
    // If the codes are not consecutive, we show multiselect
    sortedCodeSuffixes.forEach((suffix, index, array) => {
      if (index > 0 && index < array.length - 1) {
        const lastSuffix = array[index - 1];
        const lastRangeEnd = parseInt(lastSuffix.split('-')[1]);
        const rangeStart = parseInt(suffix.split('-')[0]);
        if (lastRangeEnd !== rangeStart && lastRangeEnd + 1 !== rangeStart && rangeStart !== 0) {
          return true;
        }
      }
    });
    // Otherwise we keep it as slider/tiles
    return false;
  }
  function setDefaultValues() {
    gender.value = props.defaultGender || genderOptions.value.find((gender) => gender.id === 'A');
    age.value = [0, ageRanges.value.length - 1];
    income.value = [0, incomeBrackets.value.length - 1];
  }
  function setSelectedValues() {
    const selectedGenders = props.selectedDemographics.filter((demo) => DemographicUtils.isGenderDemographic(demo));
    const selectedAgeRanges = props.selectedDemographics.filter((demo) => DemographicUtils.isAgeDemographic(demo));
    const selectedIncomeBrackets = props.selectedDemographics.filter((demo) =>
      DemographicUtils.isIncomeDemographic(demo),
    );
    const selectedEthnicities = props.selectedDemographics.filter((demo) =>
      DemographicUtils.isEthnicityDemographic(demo),
    );

    if (selectedGenders?.length) {
      let genderOption;
      if (props.isCensus) {
        const code = selectedGenders[0].code;
        genderOption = Genders.options().find((g) => code.startsWith(g.id));
      } else {
        const code = selectedGenders[0].code.split(genderCode)[1];
        genderOption = Genders.options().find((g) => g.id === code);
      }
      genderOption.entity = selectedGenders[0];
      gender.value = genderOption;
    } else if (selectedAgeRanges.length && selectedAgeRanges.every((x) => x.isAgeGender())) {
      const genderIDs = uniq(selectedAgeRanges.map((x) => x.code[0]));
      if (genderIDs.length === 1) {
        gender.value = Genders.options().find((g) => g.id === genderIDs[0]);
      }
    } else {
      gender.value = Genders.options().find((g) => g.id === 'A');
    }

    if (selectedEthnicities?.length) {
      if (props.isCensus) {
        ethnicities.value = selectedEthnicities.map((demo) => ({
          name: demo.name,
          id: demo.code,
          demo,
        }));
      }
    }

    if (selectedAgeRanges?.length) {
      const ranges = selectedAgeRanges.map((a) => DemographicUtils.getAgeRange(a));

      let start: string, end: string;
      if (props.isCensus) {
        start = first(ranges).start;
        end = last(ranges).end;
      } else {
        const range = first(ranges);
        start = range.start;
        end = range.end;
      }

      const startIndex = ageRanges.value.indexOf(start);
      const endIndex = ageRanges.value.findIndex((value) => {
        return end === value || Number(end) + 1 === Number(value);
      });
      age.value = [startIndex, endIndex];
    } else {
      age.value = [0, ageRanges.value.length - 1];
    }

    if (selectedIncomeBrackets?.length) {
      const ranges = HouseholdIncomeRanges.optionsFormatted(selectedIncomeBrackets);
      const start = first(ranges);
      const end = last(ranges);
      const startIndex = incomeBrackets.value.indexOf(start);
      const endIndex = incomeBrackets.value.indexOf(end);
      income.value = [startIndex, endIndex];
    } else {
      income.value = [0, incomeBrackets.value.length - 1];
    }
  }

  // used for display only - some legacy behaviour
  function setReadOnlyValues(): void {
    const [selectedDemo] = props.selectedDemographics;
    const codes = DemographicUtils.getCodesFromCombinationCode(selectedDemo);
    const ageC = codes.find((c) => c.includes(DemographicUtils.codes.age));
    const genderC = codes.find((c) => c.includes(DemographicUtils.codes.gender));
    gender.value = Genders.options().find((g) => g.id === genderC.split(`${DemographicUtils.codes.gender}`)[1]);
    const { start, end } = DemographicUtils.getAgeRange({
      code: `${DemographicUtils.codes.demographic} ${ageC}`,
    } as Demographic);
    const range = end === '+' ? `${start}+` : `${start} - ${end}`;
    // if we are in readonly with selectedDemographics value, use the selectedGender name to also display the targeted age range
    gender.value.name = `${gender.value.name}: ${range}`;
  }

  function emitDemographics(): void {
    // Don't emit while initializing
    if (isInitializing.value) return;

    const newDemographics = [];

    const isAllGenders = !gender.value || gender.value.id === 'A';
    const isDefaultAge = areArraysEqual(age.value, [first(ageMarks.value), last(ageMarks.value)]);
    if (!isDefaultAge) {
      const [start, end] = age.value;
      const startRange = Number(ageRanges.value[start]);
      const endRange = ageRanges.value[end] === '+' ? '' : Number(ageRanges.value[end]);
      if (props.isCensus) {
        const isUngenderedWithNoAgeOnlyDemos = isAllGenders && !hasAgeOnlyDemographics.value;
        const isGenderedWithNoGenderedDemos = !isAllGenders && !hasAgeGenderDemographics.value;
        // Census works differently than scentre / kantar, needs its own logic
        ageDemographics.value.forEach((d) => {
          let demoGender: string, code: string;
          const demoHasPrefix = /^[MFE] /.test(d.code);
          const isGenderedDemo = /^[MF] /.test(d.code);
          if (demoHasPrefix) {
            const splitCode = d.code.split(' ');
            if (isGenderedDemo) demoGender = splitCode[0];
            code = splitCode[1];
          } else {
            code = d.code;
          }
          const matchingAgeOnlyDemo = isAllGenders && !isGenderedDemo;
          const matchingGenderedDemo = !isAllGenders && gender.value.id === demoGender;
          if (
            matchingAgeOnlyDemo ||
            isUngenderedWithNoAgeOnlyDemos ||
            isGenderedWithNoGenderedDemos ||
            matchingGenderedDemo
          ) {
            if (code.includes('-')) {
              const [dStart, dEnd] = code.split('-');
              // Add in range
              if (Number(dStart) >= startRange && (!endRange || Number(dEnd) <= endRange)) {
                newDemographics.push(d);
              }
            } else if (!endRange) {
              // Add last one if no end to range
              newDemographics.push(d);
            }
          }
        });
      } else {
        // Only one demo if not census
        const range = `[${startRange},${endRange}]`;
        const ageDemographic = ageDemographics.value.find((d) => d.code.includes(range));
        if (ageDemographic) {
          newDemographics.push(ageDemographic);
        }
      }
    }

    const isDefaultIncome = areArraysEqual(income.value, [first(incomeMarks.value), last(incomeMarks.value)]);
    if (!isDefaultIncome) {
      const [start, end] = income.value;
      const startRange = Number(incomeBrackets.value[start]);
      const endRange = incomeBrackets.value[end] === '+' ? '' : Number(incomeBrackets.value[end]);
      if (props.isCensus) {
        // Census works differently than scentre / kantar, needs its own logic
        incomeDemographics.value.forEach((d) => {
          const code = d.code.split(' ')[1];
          // all except last one should be something like 12-24
          if (code.includes('-')) {
            const [dStart, dEnd] = code.split('-');
            // Add in range
            if (Number(dStart) >= startRange && (!endRange || Number(dEnd) <= endRange)) {
              newDemographics.push(d);
            }
          } else if (!endRange) {
            // Add last one if no end to range
            newDemographics.push(d);
          }
        });
      } else {
        // Only one demo if not census
        const range = `[${startRange},${endRange}]`;
        const incomeDemographic = incomeDemographics.value.find((d) => d.code.includes(range));
        if (incomeDemographic) {
          newDemographics.push(incomeDemographic);
        }
      }
    }

    // Dont push age demo if we have selected age-gdr demos
    if (!isAllGenders && (isDefaultAge || !hasAgeGenderDemographics.value)) {
      newDemographics.push(gender.value.entity);
    }
    if (ethnicities.value) {
      newDemographics.push(...ethnicities.value.map((eth) => eth.demo));
    }

    emit('onChange', newDemographics);

    // reset
    if (isDefaultAge && isDefaultIncome && gender.value?.id === 'A' && !ethnicities.value) {
      emit('onChange', []);
    }
  }
  function onGenderSelectionChange([genderSelection]): void {
    gender.value = genderSelection;
    emitDemographics();
  }

  //
  // Watchers
  //
  watch(age, () => {
    emitDemographics();
  });
  watch(income, () => {
    emitDemographics();
  });
  watch(ethnicities, () => {
    emitDemographics();
  });

  //
  // Mounted
  //
  onMounted(async () => {
    if (props.selectedDemographics?.length) {
      if (props.isReadOnly && DemographicUtils.isCombination(props.selectedDemographics[0])) {
        setReadOnlyValues();
      } else {
        setSelectedValues();
      }
    } else {
      setDefaultValues();
    }
    if (!props.isReadOnly) {
      fallbackToMultiselect.value = checkFallbackToMultiselect(); // no need to set this for readonly
    }
    // Wait a tick to mark as initialized
    await nextTick();
    isInitializing.value = false;
  });
</script>

<style lang="scss" rel="stylesheet/scss" scoped>
  @use '@/styles/variables' as *;

  .slider-container {
    display: flex;
    flex-direction: row;
    align-items: center;

    svg {
      height: 4.8rem;
      width: 4.8rem;

      &:first-of-type {
        margin-right: 2.4rem;
      }

      &:last-of-type {
        margin-left: 2.4rem;
      }
    }

    .vue-slider {
      flex-grow: 1;
      margin-right: 3.2rem;
    }

    .alert-danger {
      margin-top: 3rem;
      margin-bottom: 0;
    }
  }

  .section-label {
    text-transform: uppercase;
    font-weight: 100;
    font-size: 0.8em;
    margin-bottom: 0.6rem;
  }

  .well {
    padding: 2.4rem 3.2rem !important;
  }
</style>
